import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Trash2, RotateCcw, Mail, CreditCard, Heart, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";

const TIER_TABS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "free", label: "Free" },
  { key: "deactivated", label: "Deactivated" },
];

export default function AdminSubscribersTab() {
  const [tab, setTab] = useState("all");
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/users?include_deleted=true");
      setUsers(r.data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = users.filter((u) => {
    if (tab === "paid" && (u.subscription_tier !== "paid" || u.is_deleted)) return false;
    if (tab === "free" && (u.subscription_tier === "paid" || u.is_deleted)) return false;
    if (tab === "deactivated" && !u.is_deleted) return false;
    if (tab === "all" && u.is_deleted) return false;
    if (search) {
      const s = search.toLowerCase();
      return (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
    }
    return true;
  });

  const counts = {
    all: users.filter((u) => !u.is_deleted).length,
    paid: users.filter((u) => u.subscription_tier === "paid" && !u.is_deleted).length,
    free: users.filter((u) => u.subscription_tier !== "paid" && !u.is_deleted).length,
    deactivated: users.filter((u) => u.is_deleted).length,
  };

  const open = async (u) => {
    setSelectedId(u.user_id);
    setLoadingDetail(true);
    try {
      const r = await api.get(`/admin/users/${u.user_id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Could not load details");
    } finally { setLoadingDetail(false); }
  };

  const setTier = async (email, tier) => {
    try {
      await api.post("/admin/set-tier", { target_email: email, tier });
      toast.success(`${email} → ${tier}`);
      refresh();
      if (detail) open({ user_id: detail.user.user_id });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.post("/admin/soft-delete-user", { target_email: deleteConfirm.email });
      toast.success(`${deleteConfirm.email} deactivated.`);
      setDeleteConfirm(null); setSelectedId(null); setDetail(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const restoreUser = async (email) => {
    try {
      await api.post("/admin/restore-user", { target_email: email });
      toast.success(`${email} restored.`);
      refresh();
      if (detail) open({ user_id: detail.user.user_id });
    } catch (err) {
      toast.error("Could not restore");
    }
  };

  const [sendingLink, setSendingLink] = useState(false);
  const sendSigninLink = async (u) => {
    setSendingLink(true);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/send-signin-link`, { app_origin: window.location.origin });
      if (r.data.sent) toast.success(`Sign-in link + instructions emailed to ${u.email}`);
      else toast.error("Email could not be sent — check Resend configuration");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send sign-in link");
    } finally { setSendingLink(false); }
  };

  const exportCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-subscribers-tab">
      <div className="hc-card p-5">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex gap-1.5 flex-wrap">
            {TIER_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm transition ${tab === t.key ? "bg-[#3A5A4A] text-white" : "bg-[#F3EFEA] text-[#7A736E] hover:bg-[#EBE5DF]"}`}
                data-testid={`subs-tier-${t.key}`}
              >
                {t.label} ({counts[t.key]})
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => exportCsv("/admin/export/users.csv?tier=free", "hc-free.csv")} className="text-xs text-[#3A5A4A] hover:underline" data-testid="export-free">Export free CSV</button>
            <button onClick={() => exportCsv("/admin/export/buyers.csv", "hc-buyers.csv")} className="text-xs text-[#3A5A4A] hover:underline" data-testid="export-buyers">Export buyers CSV</button>
          </div>
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A736E]" />
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-xl border-[#EBE5DF]"
            data-testid="subs-search"
          />
        </div>

        <div className="space-y-0.5">
          {filtered.length === 0 && <p className="text-sm text-[#7A736E] py-8 text-center">No subscribers in this view.</p>}
          {filtered.map((u) => (
            <button
              key={u.user_id}
              onClick={() => open(u)}
              className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F3EFEA] transition group ${u.is_deleted ? "opacity-50" : ""}`}
              data-testid={`sub-row-${u.email}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{u.name || "—"}</p>
                <p className="text-xs text-[#7A736E] truncate">{u.email}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${u.subscription_tier === "paid" ? "bg-[#D8E2D8] text-[#3A5A4A]" : "bg-[#F3EFEA] text-[#7A736E]"}`}>
                {u.subscription_tier || "free"}
              </span>
              {u.founding_member && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[#3A5A4A] to-[#2C4538] text-[#E8DCC8] font-medium flex-shrink-0" title={`Founder #${u.founding_member_number}`} data-testid={`sub-founder-badge-${u.email}`}>
                  ★ #{u.founding_member_number}
                </span>
              )}
              {u.is_deleted && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FBEFEB] text-[#C87B67] flex-shrink-0">Deactivated</span>}
              <ChevronRight className="w-4 h-4 text-[#7A736E] group-hover:text-[#3A5A4A] flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>

      {/* Detail drawer */}
      {selectedId && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={() => { setSelectedId(null); setDetail(null); }} data-testid="sub-detail-drawer">
          <div
            className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-[#EBE5DF] flex items-center justify-between">
              <div>
                {loadingDetail || !detail ? (
                  <h3 className="font-display text-xl">Loading…</h3>
                ) : (
                  <>
                    <h3 className="font-display text-xl">{detail.user.name || "—"}</h3>
                    <p className="text-sm text-[#7A736E]">{detail.user.email}</p>
                  </>
                )}
              </div>
              <button onClick={() => { setSelectedId(null); setDetail(null); }} className="p-2 hover:bg-[#F3EFEA] rounded-lg" data-testid="sub-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detail && (
              <div className="p-6 space-y-6">
                {/* Profile + actions */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex gap-2 flex-wrap text-xs">
                    <span className={`px-2.5 py-1 rounded-full ${detail.user.subscription_tier === "paid" ? "bg-[#D8E2D8] text-[#3A5A4A]" : "bg-[#F3EFEA] text-[#7A736E]"}`}>{detail.user.subscription_tier || "free"}</span>
                    {detail.user.founding_member && (
                      <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-[#3A5A4A] to-[#2C4538] text-[#E8DCC8] font-medium" data-testid="sub-detail-founder">
                        ★ Founding Member #{detail.user.founding_member_number}
                      </span>
                    )}
                    <span className="px-2.5 py-1 rounded-full bg-[#F3EFEA] text-[#7A736E]" data-testid="sub-auth-provider">
                      Signs in with: {detail.user.auth_provider === "google" ? "Google" : detail.user.auth_provider === "magic_link" ? "Email link (no password)" : detail.user.auth_provider === "password" ? "Email + password" : detail.user.auth_provider || "unknown"}
                    </span>
                    {detail.user.is_deleted && <span className="px-2.5 py-1 rounded-full bg-[#FBEFEB] text-[#C87B67]">Deactivated</span>}
                    {detail.user.phone_verified && <span className="px-2.5 py-1 rounded-full bg-[#E8DCC8] text-[#3A5A4A]">📱 SMS verified</span>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {detail.user.is_deleted ? (
                      <Button onClick={() => restoreUser(detail.user.email)} variant="outline" className="rounded-full border-[#EBE5DF] text-xs" data-testid="sub-restore">
                        <RotateCcw className="w-3 h-3 mr-1" /> Restore
                      </Button>
                    ) : (
                      <>
                        <Button onClick={() => sendSigninLink(detail.user)} disabled={sendingLink} variant="outline" className="rounded-full border-[#EBE5DF] text-xs" data-testid="sub-send-signin-link">
                          <Mail className="w-3 h-3 mr-1" /> {sendingLink ? "Sending…" : "Email sign-in link"}
                        </Button>
                        {detail.user.subscription_tier !== "paid" ? (
                          <Button onClick={() => setTier(detail.user.email, "paid")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-xs" data-testid="sub-make-paid">
                            Make Premium
                          </Button>
                        ) : (
                          <Button onClick={() => setTier(detail.user.email, "free")} variant="outline" className="rounded-full border-[#EBE5DF] text-xs" data-testid="sub-revert-free">
                            Revert to Free
                          </Button>
                        )}
                        <Button onClick={() => setDeleteConfirm(detail.user)} variant="outline" className="rounded-full border-[#FBEFEB] text-[#C87B67] hover:bg-[#FBEFEB] text-xs" data-testid="sub-deactivate">
                          <Trash2 className="w-3 h-3 mr-1" /> Deactivate
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Email engagement */}
                <section>
                  <h4 className="font-display text-lg mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[#3A5A4A]" /> Email engagement</h4>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-[#F3EFEA]">
                      <p className="text-xs text-[#7A736E]">Sent</p>
                      <p className="font-display text-2xl">{detail.sends.total}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#F3EFEA]">
                      <p className="text-xs text-[#7A736E]">Open rate</p>
                      <p className="font-display text-2xl">{detail.sends.open_rate.toFixed(0)}%</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#F3EFEA]">
                      <p className="text-xs text-[#7A736E]">Click rate</p>
                      <p className="font-display text-2xl">{detail.sends.click_rate.toFixed(0)}%</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {detail.sends.recent.length === 0 && <p className="text-xs text-[#7A736E]">No emails sent yet.</p>}
                    {detail.sends.recent.map((s) => (
                      <div key={s.id} className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg border border-[#EBE5DF] text-xs" data-testid={`send-${s.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{s.subject}</p>
                          <p className="text-[10px] text-[#7A736E]">{new Date(s.sent_at).toLocaleString()} · {s.category}</p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          {s.opened_at && <span className="px-1.5 py-0.5 rounded bg-[#D8E2D8] text-[#3A5A4A] text-[10px]">Opened {s.open_count}×</span>}
                          {s.click_count > 0 && <span className="px-1.5 py-0.5 rounded bg-[#E8DCC8] text-[#3A5A4A] text-[10px]">{s.click_count} click</span>}
                          {!s.opened_at && <span className="px-1.5 py-0.5 rounded bg-[#F3EFEA] text-[#7A736E] text-[10px]">Sent</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Stripe payments */}
                <section>
                  <h4 className="font-display text-lg mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#3A5A4A]" /> Payment history</h4>
                  {detail.payments.length === 0 ? (
                    <p className="text-xs text-[#7A736E]">No payments yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {detail.payments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-[#EBE5DF] text-xs" data-testid={`payment-${p.id}`}>
                          <div>
                            <p className="font-medium">${p.amount.toFixed(2)} {p.currency.toUpperCase()}</p>
                            <p className="text-[10px] text-[#7A736E]">{new Date(p.created).toLocaleString()} · {p.status}</p>
                          </div>
                          {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-[#3A5A4A] hover:underline">Receipt →</a>}
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* Hope Chests + referrals */}
                <section className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-[#F3EFEA]">
                    <p className="text-xs uppercase tracking-wider text-[#7A736E] mb-1.5">Hope Chests</p>
                    <p className="font-display text-2xl">{detail.chests.length}</p>
                    <ul className="mt-2 text-xs text-[#7A736E] space-y-0.5">
                      {detail.chests.map((c) => <li key={c.id} className="truncate">• {c.name}</li>)}
                    </ul>
                  </div>
                  <div className="p-4 rounded-xl bg-[#F3EFEA]">
                    <p className="text-xs uppercase tracking-wider text-[#7A736E] mb-1.5">Referrals made</p>
                    <p className="font-display text-2xl">{detail.referrals_made}</p>
                    {detail.user.referral_code && <p className="text-[10px] text-[#7A736E] mt-1 font-mono truncate">code: {detail.user.referral_code}</p>}
                  </div>
                </section>

                {/* Feedback they've left */}
                {detail.feedback.length > 0 && (
                  <section>
                    <h4 className="font-display text-lg mb-3">Their feedback ({detail.feedback.length})</h4>
                    <div className="space-y-2">
                      {detail.feedback.map((f) => (
                        <div key={f.id} className="p-3 rounded-lg border border-[#EBE5DF] text-xs">
                          <div className="flex justify-between mb-1">
                            <span className="capitalize font-medium text-[#3A5A4A]">{f.category}</span>
                            <span className="text-[#7A736E]">{new Date(f.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="whitespace-pre-wrap">{f.message}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg mb-2">Deactivate {deleteConfirm.email}?</h3>
            <p className="text-sm text-[#7A736E] mb-5">
              All emails (drip, broadcast, digest, nudge) will stop. Their login will be blocked. Their data stays safe and recoverable.
            </p>
            <div className="flex gap-2 justify-end">
              <Button onClick={() => setDeleteConfirm(null)} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="delete-cancel">No, keep them</Button>
              <Button onClick={confirmDelete} className="rounded-full bg-[#C87B67] hover:bg-[#A85F4D] text-white" data-testid="delete-confirm">Yes, deactivate</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
